const EMBED_TOKEN_KEY = 'dq_embed_token';

export function getAuthHeader() {
  try {
    const token = localStorage.getItem(EMBED_TOKEN_KEY);
    if (!token) return null;
    return `Bearer ${token}`;
  } catch {
    return null;
  }
}

export function setEmbedToken(token) {
  localStorage.setItem(EMBED_TOKEN_KEY, token);
}

export function clearEmbedToken() {
  localStorage.removeItem(EMBED_TOKEN_KEY);
}

export function getImpersonationHeader() {
  try {
    const raw = localStorage.getItem('dq_impersonation');
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data.accountId || !data.expiresAt || data.expiresAt <= Date.now()) return null;
    return data.accountId;
  } catch {
    return null;
  }
}

async function handleImpersonationForbidden(res) {
  if (res.status === 403) {
    try {
      const json = await res.clone().json();
      if (json.message === 'Impersonation session has expired') {
        const err = new Error('Impersonation session has expired');
        err.isImpersonationExpired = true;
        throw err;
      }
      if (json.message === 'Route not allowed for employee impersonation') {
        const err = new Error('Employee impersonation restricted');
        err.isImpersonationRestricted = true;
        throw err;
      }
    } catch (err) {
      if (err.isImpersonationRestricted || err.isImpersonationExpired) throw err;
      // JSON parse failed, continue normal handling
    }
  }
}

export async function getJson(url) {
  const headers = {};
  const imp = getImpersonationHeader();
  if (imp) headers['X-DQ-Impersonation'] = imp;
  const auth = getAuthHeader();
  if (auth) headers['Authorization'] = auth;

  const res = await fetch(process.env.VUE_APP_API + url, {
    credentials: 'include',
    method: 'GET',
    headers
  });

  // Handle 403 - Employee impersonation restriction
  await handleImpersonationForbidden(res);

  // Handle 401 - Permission Denied
  if (res.status == 401) {
    throw Error('Permission denied');
  }

  const json = await res.json();
  if (res.status != 200) {
    throw Error(json.message || res.statusText);
  }
  return json;
}

export async function postJson(url, body) {
  const headers = { 'Content-Type': 'application/json' };
  const imp = getImpersonationHeader();
  if (imp) headers['X-DQ-Impersonation'] = imp;
  const auth = getAuthHeader();
  if (auth) headers['Authorization'] = auth;

  const res = await fetch(process.env.VUE_APP_API + url, {
    credentials: 'include',
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  // Handle 403 - Employee impersonation restriction
  await handleImpersonationForbidden(res);

  // Handle 404 - does not exist
  if (res.status == 404) {
    throw Error('Does Not Exist');
  }

  // Handle 401 - Permission Denied
  if (res.status == 401) {
    throw Error('Permission denied');
  }

  const json = await res.json();
  if (res.status != 200) {
    throw Error(json.message || res.statusText);
  }
  return json;
}

export async function postForm(url, formData) {
  const headers = {};
  const imp = getImpersonationHeader();
  if (imp) headers['X-DQ-Impersonation'] = imp;
  const auth = getAuthHeader();
  if (auth) headers['Authorization'] = auth;

  const res = await fetch(process.env.VUE_APP_API + url, {
    credentials: 'include',
    method: 'POST',
    headers,
    body: formData,
  });

  // Handle 403 - Employee impersonation restriction
  await handleImpersonationForbidden(res);

  // Handle 401 - Permission Denied
  if (res.status == 401) {
    throw Error('Permission denied');
  }

  const json = await res.json();
  if (res.status != 200) {
    throw Error(json.message || res.statusText);
  }
  return json;
}

// Read a Server-Sent Events (text/event-stream) response body to the end, invoking
// `onEvent(parsedJson)` for each `data: {...}` frame as it arrives.
async function consumeSseStream(res, onEvent) {
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const emitFrame = (frame) => {
    const trimmed = frame.trim();
    if (!trimmed.startsWith('data:')) return;
    const json = trimmed.slice(trimmed.indexOf('data:') + 'data:'.length).trim();
    if (json) onEvent(JSON.parse(json));
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      emitFrame(buffer.slice(0, sep));
      buffer = buffer.slice(sep + 2);
    }
  }
  // Flush any trailing frame that lacked a terminating blank line.
  emitFrame(buffer);
}

// POST a multipart form and consume a Server-Sent Events (text/event-stream) response, invoking
// `onEvent(parsedJson)` for each `data: {...}` frame as it arrives. Used for the streaming fax upload,
// where the backend flushes a batch of pages at a time. A pre-stream failure comes back as a normal
// JSON error and is thrown. Resolves once the stream ends.
export async function postFormStream(url, formData, onEvent) {
  const headers = {};
  const imp = getImpersonationHeader();
  if (imp) headers['X-DQ-Impersonation'] = imp;
  const auth = getAuthHeader();
  if (auth) headers['Authorization'] = auth;

  const res = await fetch(process.env.VUE_APP_API + url, {
    credentials: 'include',
    method: 'POST',
    headers,
    body: formData,
  });

  await handleImpersonationForbidden(res);
  if (res.status == 401) throw Error('Permission denied');

  // Validation / limit failures happen before the stream opens → a normal JSON error body.
  if (res.status != 200) {
    let message = res.statusText;
    try { message = (await res.json()).message || message; } catch { /* non-JSON body */ }
    throw Error(message);
  }

  await consumeSseStream(res, onEvent);
}

// POST a JSON body and consume a Server-Sent Events response — JSON sibling of postFormStream.
// Used for the streaming fax download, where the backend flushes compile progress and then the
// finished PDF. An optional AbortSignal cancels mid-stream: fetch()/reader.read() reject with an
// AbortError DOMException and no further onEvent calls occur.
export async function postJsonStream(url, body, onEvent, signal = null) {
  const headers = { 'Content-Type': 'application/json' };
  const imp = getImpersonationHeader();
  if (imp) headers['X-DQ-Impersonation'] = imp;
  const auth = getAuthHeader();
  if (auth) headers['Authorization'] = auth;

  const res = await fetch(process.env.VUE_APP_API + url, {
    credentials: 'include',
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  });

  await handleImpersonationForbidden(res);
  if (res.status == 401) throw Error('Permission denied');

  // Validation failures happen before the stream opens → a normal JSON error body.
  if (res.status != 200) {
    let message = res.statusText;
    try { message = (await res.json()).message || message; } catch { /* non-JSON body */ }
    throw Error(message);
  }

  await consumeSseStream(res, onEvent);
}